import handleError from '@fuse/core/errorHandler';
import { Button, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  useUpdateSpOrganizationMutation,
  useCreateSpOrganizationMutation,
} from 'src/app/services/ticketsApi';

const defaultValues = {
  email: '',
  guid: '',
  headOrgId: null,
  id: 0,
  okpo: '',
  shortName: '',
  title: '',
  webPage: '',
  workAddress: '',
  workCity: 'Ташкент',
  workCountry: 'Узбекистан',
  workFax: '',
  workPhone: '',
  workState: 'Узбекистан',
  inn: '',
};

const ALLOWED_PATTERN = /^[\p{L}\p{N}]/u;

const OrganizationsForm = ({ position, isEdit, onClose, refetchList }) => {
  const { t } = useTranslation('translations');
  const [save, { isLoading }] = useUpdateSpOrganizationMutation();
  const [create, { isLoading: isCreateLoading }] = useCreateSpOrganizationMutation();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues: position ?? defaultValues,
  });

  const handleClose = () => {
    onClose?.();
    reset(defaultValues);
  };

  const trimFormValues = (data) =>
    Object.fromEntries(
      Object.entries(data).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
    );

  const handleCreate = async (values) => {
    create(values)
      .unwrap()
      .then(() => {
        refetchList?.();
        handleClose();
      })
      .catch((err) => handleError({ response: err }));
  };

  const handleEdit = async (values) => {
    const { id } = values;
    save({ body: values, id })
      .unwrap()
      .then(() => {
        refetchList?.();
        handleClose();
      })
      .catch((err) => handleError({ response: err }));
  };

  const onSubmit = (values) => {
    const trimmedData = trimFormValues(values);
    if (isEdit) handleEdit(trimmedData);
    else handleCreate(trimmedData);
  };

  const makeTextRules = (required = true) => ({
    ...(required ? { required: t('RequiredField') || 'Required' } : {}),
    validate: (value) => {
      const v = (value ?? '').trim();
      if (required && !v) return t('RequiredField') || 'Required';
      if (v && !ALLOWED_PATTERN.test(v)) {
        return (
          t('SpecialCharactersNotAllowed') ||
          "Only letters, numbers, spaces and - . , _ ( ) / & : + ' are allowed"
        );
      }
      return true;
    },
  });

  const onChangeTrimStart = (fieldOnChange) => (e) => {
    fieldOnChange(e.target.value.trimStart());
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="max-w-5xl pt-10">
      <div className="flex flex-row justify-between gap-16 mb-5">
        <div className="flex flex-col gap-16">
          <Controller
            name="title"
            control={control}
            rules={makeTextRules(true)}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={t('Title')}
                error={!!errors.title}
                helperText={errors.title?.message}
                variant="outlined"
                required
                onChange={onChangeTrimStart(field.onChange)}
              />
            )}
          />
          <Controller
            name="shortName"
            control={control}
            rules={makeTextRules(true)}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={t('ShortTitle')}
                error={!!errors.shortName}
                helperText={errors.shortName?.message}
                variant="outlined"
                required
                inputProps={{ maxLength: 50 }}
                onChange={onChangeTrimStart(field.onChange)}
              />
            )}
          />
          <Controller
            name="workAddress"
            control={control}
            rules={makeTextRules(false)}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={t('WorkAddress')}
                error={!!errors.workAddress}
                helperText={errors.workAddress?.message}
                variant="outlined"
                onChange={onChangeTrimStart(field.onChange)}
              />
            )}
          />
          <Controller
            name="workCountry"
            control={control}
            rules={makeTextRules(true)}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={t('WorkCountry')}
                error={!!errors.workCountry}
                helperText={errors.workCountry?.message}
                variant="outlined"
                required
                onChange={onChangeTrimStart(field.onChange)}
              />
            )}
          />
          <Controller
            name="workCity"
            control={control}
            rules={makeTextRules(false)}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={t('WorkCity')}
                error={!!errors.workCity}
                helperText={errors.workCity?.message}
                variant="outlined"
                onChange={onChangeTrimStart(field.onChange)}
              />
            )}
          />
        </div>
        <div className="flex flex-col gap-16">
          <Controller
            name="webPage"
            control={control}
            rules={{
              required: false,
              pattern: {
                value: /^(https?:\/\/)?([a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*)(:[0-9]{1,5})?(\/[^\s]*)?$/,
                message: t('EnterAValidURL'),
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={t('WebPage')}
                error={!!errors.webPage}
                helperText={errors.webPage?.message}
                variant="outlined"
                onChange={onChangeTrimStart(field.onChange)}
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            rules={{
              required: false,
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                message: t('EnterValidEmail'),
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={t('Email')}
                error={!!errors.email}
                helperText={errors.email?.message}
                variant="outlined"
                inputProps={{ maxLength: 50 }}
                onChange={onChangeTrimStart(field.onChange)}
              />
            )}
          />
          <Controller
            name="workPhone"
            control={control}
            rules={{
              required: false,
              pattern: {
                value: /^\+\d{12}$/,
                message: t('EnterAValidPhoneNumber'),
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={t('Phone')}
                error={!!errors.workPhone}
                helperText={errors.workPhone?.message}
                placeholder="+XXXXXXXXXXXX"
                variant="outlined"
                inputProps={{ maxLength: 13 }}
                onChange={onChangeTrimStart(field.onChange)}
              />
            )}
          />
          <Controller
            name="workFax"
            control={control}
            rules={makeTextRules(false)}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={t('Fax')}
                error={!!errors.workFax}
                helperText={errors.workFax?.message}
                variant="outlined"
                onChange={onChangeTrimStart(field.onChange)}
              />
            )}
          />
          <Controller
            name="inn"
            control={control}
            rules={{
              required: t('RequiredField'),
              validate: (value) => {
                if (!/^\d+$/.test(value)) return t('InnMustBeNumeric');
                if (value.length !== 9) return t('InnMustBe9Digits');
                return true;
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={t('Inn')}
                error={!!errors.inn}
                helperText={errors.inn?.message}
                variant="outlined"
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 9 }}
                onChange={(e) => {
                  const onlyNumbers = e.target.value.replace(/\D/g, '').slice(0, 9);
                  field.onChange(onlyNumbers);
                }}
              />
            )}
          />
        </div>
      </div>
      <Button
        disabled={isLoading || isCreateLoading || !isValid}
        type="submit"
        className="w-full"
        variant="contained"
        color="secondary"
      >
        <span className="mx-8">{t('Save')}</span>
      </Button>
    </form>
  );
};

export default OrganizationsForm;
